import * as _ from '@reolink-web/utils';
import store from '@/store';
import 'crypto-js/core';
import WordArray from 'crypto-js/lib-typedarrays';
import HmacSHA256 from 'crypto-js/hmac-sha256';
import AES from 'crypto-js/aes';
import CFB from 'crypto-js/mode-cfb';
import NoPadding from 'crypto-js/pad-nopadding';
import Utf8 from 'crypto-js/enc-utf8';
import OpenSSL from 'crypto-js/format-openssl';
import { IRequestConfig } from '../interface';
import { AxiosResponse } from 'axios';
import { HttpClient } from '../client';

const VERSION = 1;

class STMAlgoV1 {

    public encrypt( plaintext: string ): { time: string; data: string; } {

        const time = Date.now().toString();

        const { key, iv } = this._getSTMv1KeyIv( time );

        const rt = AES.encrypt( plaintext, WordArray.create( key ), {
            iv: WordArray.create( iv ),
            mode: CFB,
            padding: NoPadding
        });
        return {
            time,
            /**
             * encrypted Data (base64 value)
             */
            data: rt.toString( OpenSSL )
        };

    }

    /**
     *
     * @param {string} time
     * @param {string} ciphertext encrypted Data (base64 value)
     * @returns
     */
    public decrypt( time: string, ciphertext: string ): string {

        const { key, iv } = this._getSTMv1KeyIv( time );

        const rt = AES.decrypt( ciphertext, WordArray.create( key ), {
            iv: WordArray.create( iv ),
            mode: CFB,
            padding: NoPadding,
            format: OpenSSL
        });
        return rt.toString( Utf8 );

    }

    /**
     * @param {string} time
     */
    private _getSTMv1KeyIv( time: string ): { key: string; iv: string; } {

        const srcData = store.getters.userId;
        if ( !srcData ) {

            throw new TypeError( `Get empty encrypt key: ${srcData}` );

        }
        const keySrc = HmacSHA256( `${store.getters.userId}`, time );
        // int32 array, get 16 bytes
        const aesKey = keySrc.words.slice( 0, 4 );
        const aesIv = keySrc.words.slice( 4, 8 );
        return {
            key: aesKey,
            iv: aesIv
        };

    }

}

let plugin: STM;
export class STM {

    private readonly _version: number;

    private readonly _algo: STMAlgoV1;

    public constructor() {

        this._version = VERSION;

        this._algo = new STMAlgoV1();

    }

    public requestInterceptor( config: IRequestConfig ): IRequestConfig {

        // 请求body可以不加密
        const encryptRequestBody = config.encryptRequestBody ?? true;
        if ( !encryptRequestBody ) {

            return config;

        }
        switch ( config.method ) {

            case 'post':
            case 'put':
                break;
            default:
                return config;

        }

        if ( config.stmOnRequest === false ) {

            return config;

        }

        if ( _.TypeAssert.isString( config.data ) && config.headers && config.headers['content-type'] === 'application/json' ) {

            config.data = this._encryptData( config.data );

        }
        else if ( _.TypeAssert.isRawObject( config.data )) {

            config.data = this._encryptData( JSON.stringify( config.data ));

        }

        return config;

    }

    public responseInterceptor( response: AxiosResponse ): AxiosResponse {

        const config = response.config as IRequestConfig;
        switch ( config.method ) {

            case 'get':
            case 'post':
                break;
            default:
                return response;

        }

        if ( config.stmOnResponse === false ) {

            return response;

        }

        if ( !response.data.stm ) {

            return response;

        }

        const { stm, time, data } = response.data;

        const rawData = this._decryptData( stm, time, data );

        try {

            response.data = JSON.parse( rawData );

        }
        catch ( _ ) {

            console.warn( 'error', _ );
            response.data = rawData;

        }

        return response;

    }

    private _encryptData( rawData: string ): { stm: number;time: string;data: string; } {

        const encrypted = this._algo.encrypt( rawData );

        return {
            stm: this._version,
            time: encrypted.time,
            data: encrypted.data
        };

    }

    private _decryptData( version: number, time: string, rawCipherData: string ): string {

        if ( this._version !== version ) {

            throw new RangeError( 'Unsupported decrypt algo version: ' + version );

        }

        const decrypted = this._algo.decrypt( time, rawCipherData );

        return decrypted;

    }

    public static register( httpClient: HttpClient ): void {

        if ( !plugin ) {

            plugin = new STM();

        }

        httpClient._client.interceptors.request.use(
            plugin.requestInterceptor.bind( plugin )
        );

        httpClient._client.interceptors.response.use(
            plugin.responseInterceptor.bind( plugin )
        );

    }

}
